import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { HELP_CONTENT } from '../data/helpContent';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import {
  HelpCircle, X, BookOpen, Settings, Lightbulb, PlayCircle,
  ChevronRight, CheckCircle, AlertTriangle, Info, Zap,
  MessageCircle, Send, Bot, User, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TAB_CONFIG = [
  { key: 'what',   label: '¿Qué es?',    icon: BookOpen },
  { key: 'steps',  label: 'Cómo usarlo', icon: PlayCircle },
  { key: 'config', label: 'Configurar',  icon: Settings },
  { key: 'tips',   label: 'Tips Pro',    icon: Lightbulb },
  { key: 'chat',   label: 'Okbot',       icon: MessageCircle },
];

const TIP_STYLES = {
  pro:     { bg: 'bg-orange-50 border-orange-200', icon: Zap,           text: 'text-orange-700', badge: 'bg-orange-100 text-orange-700', label: 'PRO' },
  warning: { bg: 'bg-amber-50 border-amber-200',   icon: AlertTriangle, text: 'text-amber-700',  badge: 'bg-amber-100 text-amber-700',  label: '⚠ OJO' },
  info:    { bg: 'bg-blue-50 border-blue-200',     icon: Info,          text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-700',    label: 'INFO' },
};

export function HelpDrawer({ section, compact = false }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('what');

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);

  const content = HELP_CONTENT[section];

  const scrollToBottom = useCallback(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (tab === 'chat') scrollToBottom();
  }, [chatMessages, tab, scrollToBottom]);

  useEffect(() => {
    if (!content) return;
    if (tab === 'chat' && chatMessages.length === 0) {
      setChatMessages([{
        role: 'assistant',
        content: `¡Hola! Soy Okbot, tu asistente de OkReserva. Estás en la sección **${content.title}**. ¿En qué puedo ayudarte?`
      }]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const sendMessage = useCallback(async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;

    const userMsg = { role: 'user', content: text };
    const updatedMessages = [...chatMessages, userMsg];
    setChatMessages(updatedMessages);
    setChatInput('');
    setChatLoading(true);

    try {
      const apiMessages = updatedMessages.map(m => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API_URL}/api/ai-agents/support-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_id: 'okbot-soporte',
          messages: apiMessages,
          session_id: sessionId
        })
      });
      const data = await res.json();
      if (data.session_id && !sessionId) setSessionId(data.session_id);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response || 'Lo siento, no pude procesar tu mensaje. Intenta de nuevo.'
      }]);
    } catch {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Error de conexión. Por favor intenta de nuevo o contáctanos en hola@okgo.mx'
      }]);
    } finally {
      setChatLoading(false);
    }
  }, [chatInput, chatLoading, chatMessages, sessionId]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  if (!content) return null;

  const hasConfig = content.config && content.config.length > 0;
  const tabs = TAB_CONFIG.filter(t => t.key !== 'config' || hasConfig);

  const drawerPortal = createPortal(
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998] transition-opacity"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Drawer Panel */}
      <div className={cn(
        'fixed top-0 right-0 z-[9999] flex flex-col bg-white shadow-2xl transition-transform duration-300',
        'w-full sm:w-[480px]',
        'h-screen',
        open ? 'translate-x-0' : 'translate-x-full'
      )}
        style={{ maxHeight: '100dvh' }}
      >

        {/* Header with gradient */}
        <div className={cn('bg-gradient-to-r text-white p-5 flex-shrink-0', content.color)}>
          <div className="flex items-start justify-between mb-2">
            <span className="text-3xl">{content.emoji}</span>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <h2 className="text-lg font-black leading-tight">{content.title}</h2>
          <p className="text-white/80 text-xs mt-0.5">{content.description}</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b bg-gray-50 flex-shrink-0 overflow-x-auto">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                'flex-1 flex flex-col items-center gap-1 py-2.5 text-xs font-semibold transition-all border-b-2 min-w-[60px]',
                tab === t.key
                  ? 'border-orange-500 text-orange-600 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className={cn(
          'flex-1 min-h-0',
          tab === 'chat' ? 'flex flex-col' : 'overflow-y-auto'
        )}>

          {/* ¿Qué es? Tab */}
          {tab === 'what' && (
            <div className="p-5 space-y-5">
              <div className={cn('rounded-2xl p-4 bg-gradient-to-br text-white', content.color)}>
                <p className="font-medium leading-relaxed text-sm">{content.what}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">¿Para qué sirve?</p>
                <div className="space-y-2">
                  {(content.steps || []).slice(0, 3).map((step, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {i + 1}
                      </div>
                      <p className="text-sm text-gray-700">{step.title}</p>
                    </div>
                  ))}
                  {(content.steps || []).length > 3 && (
                    <button onClick={() => setTab('steps')} className="w-full flex items-center justify-center gap-1 py-2 text-sm text-orange-500 hover:text-orange-600 font-medium">
                      Ver todos los pasos <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {content.tips?.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Tip destacado</p>
                  {(() => {
                    const tip = content.tips.find(t => t.type === 'pro') || content.tips[0];
                    const style = TIP_STYLES[tip.type] || TIP_STYLES.info;
                    return (
                      <div className={cn('rounded-xl p-4 border flex gap-3', style.bg)}>
                        <style.icon className={cn('w-4 h-4 flex-shrink-0 mt-0.5', style.text)} />
                        <p className={cn('text-sm', style.text)}>{tip.text}</p>
                      </div>
                    );
                  })()}
                  <button onClick={() => setTab('tips')} className="mt-2 w-full flex items-center justify-center gap-1 py-2 text-sm text-orange-500 hover:text-orange-600 font-medium">
                    Ver todos los tips <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Cómo usarlo Tab */}
          {tab === 'steps' && (
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-500">Sigue estos pasos para aprovechar al máximo esta sección:</p>
              <div className="space-y-3">
                {(content.steps || []).map((step, i) => (
                  <div key={i} className="flex gap-4 group">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-white text-sm font-black flex items-center justify-center flex-shrink-0 shadow-sm">
                        {i + 1}
                      </div>
                      {i < (content.steps.length - 1) && <div className="w-0.5 flex-1 bg-orange-100 mt-2 mb-0" />}
                    </div>
                    <div className="flex-1 pb-4">
                      <p className="font-bold text-gray-900 mb-1">{step.title}</p>
                      <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
                {(content.steps || []).length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <PlayCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>Esta sección no tiene pasos específicos. Consulta los Tips para sacarle el mayor provecho.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Configuración Tab */}
          {tab === 'config' && (
            <div className="p-5 space-y-4">
              {content.config && content.config.length > 0 ? (
                <>
                  <p className="text-sm text-gray-500">Opciones de configuración disponibles:</p>
                  <div className="space-y-3">
                    {content.config.map((cfg, i) => (
                      <div key={i} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="flex items-start gap-3">
                          <Settings className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-bold text-gray-900 text-sm">{cfg.title}</p>
                            <p className="text-sm text-gray-600 mt-1 leading-relaxed">{cfg.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Settings className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>Esta sección no requiere configuración adicional.</p>
                  <p className="text-sm mt-1">Funciona lista para usar desde el primer día.</p>
                </div>
              )}
            </div>
          )}

          {/* Tips Pro Tab */}
          {tab === 'tips' && (
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-500">Recomendaciones de expertos para sacar el máximo provecho:</p>
              {(content.tips || []).length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Lightbulb className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>Sin tips adicionales para esta sección.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {content.tips.map((tip, i) => {
                    const style = TIP_STYLES[tip.type] || TIP_STYLES.info;
                    return (
                      <div key={i} className={cn('rounded-xl p-4 border flex gap-3', style.bg)}>
                        <div className="flex flex-col items-start gap-2 flex-shrink-0">
                          <style.icon className={cn('w-4 h-4', style.text)} />
                        </div>
                        <div className="flex-1">
                          <Badge className={cn('text-xs mb-2', style.badge)}>{style.label}</Badge>
                          <p className={cn('text-sm leading-relaxed', style.text)}>{tip.text}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-4 p-4 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl text-white">
                <p className="font-bold text-sm mb-1">¿Necesitas más ayuda?</p>
                <p className="text-xs text-white/80">Usa el chat de Okbot para resolver tus dudas al instante.</p>
                <button onClick={() => setTab('chat')} className="mt-2 text-xs font-bold text-white underline">
                  Abrir chat →
                </button>
              </div>
            </div>
          )}

          {/* Chat Tab */}
          {tab === 'chat' && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
                {chatMessages.map((msg, i) => (
                  <div key={i} className={cn('flex gap-2.5', msg.role === 'user' ? 'flex-row-reverse' : 'flex-row')}>
                    <div className={cn(
                      'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center',
                      msg.role === 'user'
                        ? 'bg-orange-100'
                        : 'bg-gradient-to-br from-orange-500 to-orange-600'
                    )}>
                      {msg.role === 'user'
                        ? <User className="w-3.5 h-3.5 text-orange-600" />
                        : <Bot className="w-3.5 h-3.5 text-white" />
                      }
                    </div>
                    <div className={cn(
                      'max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
                      msg.role === 'user'
                        ? 'bg-orange-500 text-white rounded-tr-sm'
                        : 'bg-gray-100 text-gray-800 rounded-tl-sm'
                    )}>
                      {msg.content.split('**').map((part, j) =>
                        j % 2 === 1 ? <strong key={j}>{part}</strong> : <span key={j}>{part}</span>
                      )}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex gap-2.5">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                      <Bot className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3">
                      <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="flex-shrink-0 p-3 border-t bg-gray-50">
                <div className="flex gap-2 items-end">
                  <textarea
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={`Pregunta sobre ${content.title}...`}
                    rows={1}
                    className="flex-1 resize-none rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent placeholder:text-gray-400"
                    style={{ maxHeight: '100px', overflowY: 'auto' }}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!chatInput.trim() || chatLoading}
                    className="flex-shrink-0 h-10 w-10 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                  >
                    <Send className="w-4 h-4 text-white" />
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5 text-center">Okbot · Asistente IA de OkReserva</p>
              </div>
            </>
          )}
        </div>

        {/* Footer (only for non-chat tabs) */}
        {tab !== 'chat' && (
          <div className="flex-shrink-0 px-5 py-3 border-t bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {tabs.map(t => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={cn(
                    'w-2 h-2 rounded-full transition-all',
                    tab === t.key ? 'bg-orange-500 w-4' : 'bg-gray-300 hover:bg-gray-400'
                  )}
                />
              ))}
            </div>
            <Button onClick={() => setOpen(false)} size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
              Entendido
            </Button>
          </div>
        )}
      </div>
    </>,
    document.body
  );

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(true)}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-sm font-medium transition-all',
          'border-gray-200 text-gray-500 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50',
          compact && 'px-2 py-1 text-xs'
        )}
        title="Ver instrucciones"
      >
        <HelpCircle className={cn('flex-shrink-0', compact ? 'w-3.5 h-3.5' : 'w-4 h-4')} />
        {!compact && <span>Instrucciones</span>}
      </button>
      {drawerPortal}
    </>
  );
}
